import { useMemo, useState, type ReactNode } from 'react';
import type { OnboardingSlide } from '../onboarding/onboardingSlides';
import {
  CATALOGO_DE_PLANES_DE_EQUIPO,
  claveDelPlanDeEquipo,
  COSTO_POR_CREDITO,
  CREDITOS_DE_AGENCIAS,
  escalonDeAgencias,
  esSuPlanDeEquipo,
  incluidoEnTodosLosPlanesDeEquipo,
  LINK_DEMO_PLANES_DE_EQUIPO,
  PRECIO_CORPORATIVO,
  precioDePlanDeEquipo,
  SOPORTE_PLANES_DE_EQUIPO,
  type PlanDeEquipoEnCatalogo,
} from '../../lib/catalogoDePlanesDeEquipo';
import { ModalQRWhatsApp, urlsWhatsApp } from '../ContactarPorWhatsApp';
import PricingCatalog, { type PlanView } from './PricingCatalog';
import { EscenaDeEquipo, FUNCIONES_DE_EQUIPO } from './equipo/escenasDeEquipo';
import { lineaImagenes, lineaVideos } from './pricingContent';

type ClaveDeEquipo = PlanDeEquipoEnCatalogo['clave'];

/**
 * Los planes de equipo (Equipos, Agencias, Corporativo) con EL MISMO catálogo que Básico, Pro
 * y Ultra — ADR-2026-10-27-planes-de-equipo-en-la-pantalla-de-planes-6 (la maqueta que aprobó
 * Imanol). En la compu, la ventana de dos paneles; en el teléfono, la pantalla oscura con las
 * cards de costado y el botón fijo.
 *
 * Donde los planes individuales muestran fotos, acá pasan solas las seis funciones de equipo de
 * la landing, cada una con su pantalla animada (`escenasDeEquipo`). Agencias elige sus créditos
 * (2.000 a 9.000) con el mismo slider de Ultra. Los datos son los de la landing.
 *
 * Como en la landing, los planes llevan a reservar una demo: todavía no hay alta self-service.
 * Quien ya tiene uno lo ve marcado "Tu plan", y su botón es hablar con Atlas.
 */
export function PlanesDeEquipo({
  planActual,
  pais,
  onClose,
  onHelp,
  onVerPlanesIndividuales,
  aviso,
}: {
  planActual: string | null;
  pais: string | null;
  onClose: () => void;
  onHelp?: () => void;
  /** Link a los planes individuales; no va para quien ya tiene un plan de equipo. */
  onVerPlanesIndividuales?: () => void;
  /** Un aviso al pie (ej. el portal de Stripe que no abrió). */
  aviso?: ReactNode;
}) {
  const actual = claveDelPlanDeEquipo(planActual);
  const [seleccionado, setSeleccionado] = useState<ClaveDeEquipo>(actual ?? 'agencias');
  const [escalon, setEscalon] = useState(() => escalonDeAgencias(planActual) ?? 0);

  // "Hablar con Atlas" como el resto de la app: en una compu, el QR para seguir desde el
  // celular; en el celular, WhatsApp directo.
  const [mensajeQR, setMensajeQR] = useState<string | null>(null);
  const contactar = (mensaje: string) => {
    if (window.matchMedia('(min-width: 1024px)').matches) setMensajeQR(mensaje);
    else window.open(urlsWhatsApp({ mensaje }).directa, '_blank', 'noopener,noreferrer');
  };

  // Las "fotos" del catálogo: una escena por función de equipo.
  const escenas: OnboardingSlide[] = useMemo(
    () =>
      FUNCIONES_DE_EQUIPO.map((f, i) => ({
        title: f.titulo,
        description: f.texto,
        image: '',
        preview: '',
        alt: f.titulo,
        escena: <EscenaDeEquipo indice={i} />,
      })),
    [],
  );

  const plans: PlanView<ClaveDeEquipo>[] = CATALOGO_DE_PLANES_DE_EQUIPO.map((plan) => {
    // En otro escalón de Agencias no es "Tu plan", pero el botón sigue siendo hablar con
    // Atlas: cambiar de escalón se habla.
    const esDeSuPlan = plan.clave === actual;
    const esElActual = esSuPlanDeEquipo(plan.clave, escalon, planActual);
    const base = {
      key: plan.clave,
      content: {
        tab: plan.nombre,
        title: plan.nombre,
        subtitle: plan.descripcion,
        credits: 0,
        generates: [],
        inherits: plan.hereda,
        features: plan.incluye,
        cta: 'Reservar una demo',
      },
      annual: { total: '', perMonth: '' },
      isCurrent: esElActual,
      ctaLabel: esDeSuPlan ? 'Hablar con Atlas' : 'Reservar una demo',
      ctaDisabled: false,
      ...(plan.badge ? { badge: plan.badge, badgeCorto: 'Recomendado' } : {}),
    };
    if (plan.clave === 'corporativo') {
      return {
        ...base,
        credits: null,
        creditsText: PRECIO_CORPORATIVO.creditos,
        creditsDetail: PRECIO_CORPORATIVO.creditosDetalle,
        generates: [],
        price: PRECIO_CORPORATIVO.precio,
        period: '',
        priceNote: PRECIO_CORPORATIVO.nota,
        ivaIncluido: false,
      };
    }
    const creditos = plan.clave === 'equipos' ? 1000 : CREDITOS_DE_AGENCIAS[escalon];
    const precio = precioDePlanDeEquipo(plan.clave, escalon, pais);
    return {
      ...base,
      credits: creditos,
      generates: [lineaImagenes(creditos), lineaVideos(creditos)],
      price: precio.precio,
      period: 'por mes',
      priceNote: precio.notaFacturaA ?? undefined,
      ivaIncluido: precio.ivaIncluido,
      // Agencias elige sus créditos con el mismo slider que Ultra: 2.000 a 9.000.
      tierSlider:
        plan.clave === 'agencias'
          ? {
              index: escalon,
              count: CREDITOS_DE_AGENCIAS.length,
              onChange: setEscalon,
              labels: CREDITOS_DE_AGENCIAS.map((c) => c.toLocaleString('es-AR')),
              labelsCortos: CREDITOS_DE_AGENCIAS.map((c) => `${c / 1000}k`),
              valueText: `${CREDITOS_DE_AGENCIAS[escalon].toLocaleString('es-AR')} créditos por mes`,
            }
          : undefined,
    };
  });

  const nombreDe = (clave: ClaveDeEquipo) =>
    CATALOGO_DE_PLANES_DE_EQUIPO.find((p) => p.clave === clave)?.nombre ?? '';

  // La letra chica, como la de los planes individuales: texto plano, que se lee igual sobre
  // el azul del teléfono y sobre el blanco de la compu.
  const nota = (
    <div className="flex flex-col items-center gap-2">
      {aviso}
      {actual ? (
        <p className="max-w-xl">
          Para sumar créditos, cambiar de plan, la tarjeta o darte de baja, escribinos y lo vemos con vos.
        </p>
      ) : null}
      <p className="max-w-xl">
        Incluido en todos los planes de equipo: {incluidoEnTodosLosPlanesDeEquipo(pais).join(' · ')}.
      </p>
      <p className="max-w-xl">
        {SOPORTE_PLANES_DE_EQUIPO.titulo}, incluido en todos los planes para equipos y agencias.
      </p>
      <p className="max-w-xl">{COSTO_POR_CREDITO}</p>
    </div>
  );

  return (
    <>
      <PricingCatalog<ClaveDeEquipo>
        variant="screen"
        slides={escenas}
        plans={plans}
        selected={seleccionado}
        onSelect={setSeleccionado}
        onSubscribe={(clave) =>
          clave === actual
            ? contactar(`Buenas, me contactaba por mi plan ${nombreDe(clave)} de Atlas.`)
            : window.open(LINK_DEMO_PLANES_DE_EQUIPO, '_blank', 'noopener,noreferrer')
        }
        processing={false}
        onClose={onClose}
        // El slider de Ultra no se usa (Agencias trae el suyo en `tierSlider`).
        ultraTier={{ index: 0, count: 1, onChange: () => {} }}
        showBillingToggle={false}
        annual={false}
        onAnnualChange={() => {}}
        // La ayuda (la pastilla de WhatsApp): la de la pantalla, o el mismo canal que
        // "Hablar con Atlas" con un mensaje sobre los planes de equipo.
        onHelp={onHelp ?? (() => contactar('Buenas, tengo una consulta sobre los planes de equipo de Atlas.'))}
        note={nota}
        onVerPlanesDeEquipo={onVerPlanesIndividuales}
        textoLinkAlPie="Ver los planes individuales"
      />
      <ModalQRWhatsApp
        abierto={mensajeQR !== null}
        onCerrar={() => setMensajeQR(null)}
        mensaje={mensajeQR ?? ''}
        titulo="Hablemos de tu plan"
      />
    </>
  );
}
